import {
	computed,
	toValue,
	type ComputedRef,
	type MaybeRefOrGetter,
} from "vue";
import {
	checkActiveRegionOnce,
	getActiveRegion,
	type ActiveRegion,
} from "./active-region";

export type ApiVersion = "Beta" | "Prod";
export type Environment = "Dev" | "Prod";
export type { ActiveRegion };

export type AwsConfiguration = {
	region: string;
	host: string;
	esriApiKey: string;
	apiUrl: string;
	restApiId: string;
	apiVersion: string;
	description: string;
	s3Buckets: {
		machineCredential: string;
		systemSoftwareUpdaterTelemetry: string;
		machineNotes: string;
		resourceDocument: string;
	};
};

type EnvironmentConfig = {
	/** Failover status flag endpoint (StatusCloudDistribution), one per account. */
	statusUrl: string;
	primary: AwsConfiguration;
	failover: AwsConfiguration;
};

const esriApiKey =
	"AAPK83d1d9964a9243e6a59bac3a6119bf47VaVA5uW-nqqXZsxDWiyy1j7yWd2UwCpXlKbmSYWdo942RQ2QZvHMGccPVEwWUbOQ";

const AwsEnvironmentConfig: Record<Environment, EnvironmentConfig> = {
	Dev: {
		statusUrl: "https://dev.status.outrun-app.com/status.json",
		primary: {
			region: "us-east-1",
			host: "a3mmt4jkn6hol3-ats.iot.us-east-1.amazonaws.com",
			esriApiKey,
			apiUrl: "https://${restApiId}.execute-api.${region}.amazonaws.com/${apiVersion}",
			restApiId: "v3vjsy12xc",
			apiVersion: "Beta",
			description:
				"Development - AWS: JcaOutrunFlightpath-Dev (963716652507)",
			s3Buckets: {
				machineCredential: "outrun-web-machinecredential-beta",
				systemSoftwareUpdaterTelemetry:
					"outrun-system-software-updater-telemetry-beta",
				machineNotes: "outrun-machine-notes-beta",
				resourceDocument: "outrun-help-files-beta",
			},
		},
		failover: {
			region: "us-west-2",
			host: "a3mmt4jkn6hol3-ats.iot.us-west-2.amazonaws.com",
			esriApiKey,
			apiUrl: "https://${restApiId}.execute-api.${region}.amazonaws.com/${apiVersion}",
			restApiId: "j1zarvwh61",
			apiVersion: "Beta",
			description:
				"Development - AWS: JcaOutrunFlightpath-Dev (963716652507)",
			s3Buckets: {
				machineCredential:
					"outrun-web-machinecredential-beta-us-west-2",
				systemSoftwareUpdaterTelemetry:
					"outrun-system-software-updater-telemetry-beta-us-west-2",
				machineNotes: "outrun-machine-notes-beta-us-west-2",
				resourceDocument: "outrun-help-files-beta-us-west-2",
			},
		},
	},
	Prod: {
		statusUrl: "https://status.outrun-app.com/status.json",
		primary: {
			region: "us-east-1",
			host: "a8m0fg7yvuu5k-ats.iot.us-east-1.amazonaws.com",
			esriApiKey,
			apiUrl: "https://${restApiId}.execute-api.${region}.amazonaws.com/${apiVersion}",
			restApiId: "uej1di59a8",
			apiVersion: "Prod",
			description: "Production - AWS: JcaOutrunFlightpath (414068118687)",
			s3Buckets: {
				machineCredential: "outrun-web-machinecredential",
				systemSoftwareUpdaterTelemetry:
					"outrun-system-software-updater-telemetry",
				machineNotes: "outrun-machine-notes",
				resourceDocument: "outrun-help-files",
			},
		},
		failover: {
			region: "us-west-2",
			host: "a8m0fg7yvuu5k-ats.iot.us-west-2.amazonaws.com",
			esriApiKey,
			apiUrl: "https://${restApiId}.execute-api.${region}.amazonaws.com/${apiVersion}",
			restApiId: "ocdisaltig",
			apiVersion: "Prod",
			description: "Production - AWS: JcaOutrunFlightpath (414068118687)",
			s3Buckets: {
				machineCredential: "outrun-web-machinecredential-us-west-2",
				systemSoftwareUpdaterTelemetry:
					"outrun-system-software-updater-telemetry-us-west-2",
				machineNotes: "outrun-machine-notes-us-west-2",
				resourceDocument: "outrun-help-files-us-west-2",
			},
		},
	},
};

// It needs to be changed during the deploy for the environment
export const defaultEnvironment: Environment = "Prod"; // When devs switch to `963716652507` it should be change to `Dev` (build scripts should be change too)
export const defaultApiVersion: ApiVersion = "Prod";

export const getAwsConfiguration = (
	environment: Environment,
): AwsConfiguration => {
	const activeRegion = getActiveRegion(environment);
	return AwsEnvironmentConfig[environment][activeRegion];
};

/**
 * Reactive version of getAwsConfiguration - use this in component/composable
 * code instead of hand-wrapping getAwsConfiguration in your own computed().
 * Re-resolves whenever the active region changes (or, if `environment` is a
 * ref/getter, whenever the environment itself changes too).
 */
export const useAwsConfiguration = (
	environment: MaybeRefOrGetter<Environment>,
): ComputedRef<AwsConfiguration> =>
	computed(() => getAwsConfiguration(toValue(environment)));

export const getAllAwsConfigurations = (): {
	Description: string;
	Name: Environment;
	LambdaAlias: string;
}[] =>
	Object.entries(AwsEnvironmentConfig).map(([env, config]) => ({
		Name: env as Environment,
		LambdaAlias: config.primary.apiVersion,
		Description: config.primary.description,
	}));

/**
 * Returns the currently resolved active region for an environment
 * ("primary" or "failover"), without triggering a status check.
 */
export const getEnvironmentActiveRegion = (
	environment: Environment,
): ActiveRegion => getActiveRegion(environment);

/**
 * Fetches the environment's failover status flag and updates the resolved
 * active region if it changed. Safe to call repeatedly (e.g. on a poll
 * interval) - on failure it leaves the last-known value untouched.
 */
export const checkActiveRegion = (
	environment: Environment,
): Promise<ActiveRegion> =>
	checkActiveRegionOnce(
		environment,
		AwsEnvironmentConfig[environment].statusUrl,
	);

/**
 * Returns the fully resolved API URL for a specific environment.
 *
 * HOW IT WORKS:
 * 1. Validates that the requested 'environment' exists in AwsEnvironmentConfig.
 * 2. Falls back to defaultEnvironment ("Beta") if the requested one is missing.
 * 3. Resolves the environment's currently-active region (primary/failover).
 * 4. Uses a Regular Expression (/\${(\w+)}/g) to find placeholders in the
 *    apiUrl (e.g., ${restApiId}, ${region}, ${apiVersion}).
 * 5. Replaces placeholders with actual values from the resolved config object.
 * 6. If an 'apiVersionOverride' is provided, it prioritizes that value specifically for the ${apiVersion} placeholder.
 */
export const getApiUrl = (
	environment: Environment,
	apiVersionOverride?: string,
): string => {
	// Validation & Fallback: Ensure the object exists, otherwise use default
	let envConfig = AwsEnvironmentConfig[environment];

	if (!envConfig) {
		console.warn(
			`Environment "${environment}" not found. Falling back to "${defaultEnvironment}".`,
		);
		envConfig = AwsEnvironmentConfig[defaultEnvironment];
	}

	const config = envConfig[getActiveRegion(environment)];

	// Check if override is provided AND is not just empty/whitespace
	const hasValidOverride =
		apiVersionOverride !== undefined && apiVersionOverride.trim() !== "";

	// Placeholder Replacement
	return config.apiUrl.replace(/\${(\w+)}/g, (_, key) => {
		// Use the override only if it's the 'apiVersion' key and the override is valid
		if (key === "apiVersion" && hasValidOverride) {
			return apiVersionOverride;
		}

		// Otherwise, look up the value inside the config object
		const value = (config as any)[key];

		// Return the value as a string, or an empty string if the key is missing
		return value !== undefined ? String(value) : "";
	});
};

export type ChatbotEndpointOptions = "dev" | "dealer" | "user";
const ChatbotEndpointConfig = {
	dev: "wss://9d08d56fk1.execute-api.us-east-1.amazonaws.com/dev/",
	dealer: "wss://vc1uecgfr0.execute-api.us-east-1.amazonaws.com/dealers/",
	user: "wss://exfv8vr9ji.execute-api.us-east-1.amazonaws.com/users/",
};

export const getChatbotUrl = (endpointSetting: ChatbotEndpointOptions) =>
	ChatbotEndpointConfig[endpointSetting];
